import { useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { api } from "../lib/api";
import { getApiErrorMessage } from "../lib/apiError";

export interface GenerationBrief {
  preset: "guide" | "storm" | "materials" | "maintenance" | "custom";
  audience: string;
  tone: string;
  facts: string;
  cta: string;
}
export const useArticleGeneration = () => {
  const qc = useQueryClient();
  const invalidate = () => {
    [
      "articles",
      "content-stats",
      "generation-progress",
      "generation-stats",
      "blog-ideas",
    ].forEach((key) => void qc.invalidateQueries({ queryKey: [key] }));
  };
  const generate = useMutation({
    mutationFn: async (payload: Record<string, unknown>) =>
      (
        await api.post<{ count: number; existing_count: number }>(
          "/api/marketing/articles/generate-bulk/",
          payload,
        )
      ).data,
    onSuccess: (data) => {
      invalidate();
      toast.success(
        `${data.count} articles queued${data.existing_count ? `; ${data.existing_count} already exist` : ""}.`,
      );
    },
    onError: (error) =>
      toast.error(
        getApiErrorMessage(
          error,
          "Could not start generation. Your selection has been kept.",
        ),
      ),
  });
  const retry = useMutation({
    mutationFn: (id: number) =>
      api.post(`/api/marketing/articles/${id}/retry-generation/`, {}),
    onSuccess: () => {
      invalidate();
      toast.success("Generation retry queued");
    },
    onError: (error) =>
      toast.error(getApiErrorMessage(error, "Could not retry generation")),
  });
  return {
    isGenerating: generate.isPending,
    generateArticle: (
      blogIdeaId: number,
      targetAreaId?: number | null,
      brief?: GenerationBrief,
    ) =>
      generate.mutateAsync({
        blog_idea_id: blogIdeaId,
        target_area_id: targetAreaId,
        generation_brief: brief,
      }),
    generateBulkArticles: (
      topicalMapId: number,
      blogIdeaIds?: number[],
      brief?: GenerationBrief,
      targetAreaId?: number | null,
    ) =>
      generate.mutateAsync({
        topical_map_id: topicalMapId,
        blog_idea_ids: blogIdeaIds,
        generation_brief: brief,
        target_area_id: targetAreaId,
      }),
    retryGeneration: retry.mutate,
  };
};
